import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Volume2, VolumeX, Music2 } from "lucide-react";
import { WEDDING } from "@/constants/testIds";

// Events that actually grant user activation, per the HTML spec: keydown,
// mousedown, pointerdown (mouse pointers only), pointerup (non-mouse), and
// touchend. `touchstart` and `scroll` do NOT — a tap on a phone fires
// pointerdown/touchstart and grants nothing, so listening only for those left
// mobile visitors unable to start the music by tapping anywhere but the button.
const START_EVENTS = ["pointerdown", "pointerup", "touchend", "click", "keydown"];

// MP3 only, deliberately. The WebM/Opus cut is ~27% smaller and every other
// browser handles it, but iOS Safari demuxes it without decoding the Opus
// stream: currentTime advances and it registers in Now Playing while the
// guest hears nothing. A silent invitation on iPhone is not worth 1.2 MB.
const MP3 = "/assets/wedding-music.mp3";
const VOLUME = 0.2;

export const MusicPlayer = () => {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;

    // iOS treats `volume` on a media element as read-only: this is honoured on
    // desktop and Android, and silently ignored on iPhone, where playback sits
    // at whatever the hardware buttons say. Attenuating there would mean
    // routing through Web Audio, which on iOS is itself a known cause of
    // playback that advances without producing sound. Encode the file quieter
    // instead if 20% is wanted on iPhone too.
    a.volume = VOLUME;
    a.loop = true;

    // Safari files page media under the "ambient" audio session, which the
    // iPhone's ring/silent switch mutes outright — the player animates as if
    // it were playing while the guest hears nothing. "playback" is the session
    // music apps use and it ignores that switch. Safari 16.4+, no-op elsewhere.
    try {
      if (navigator.audioSession) navigator.audioSession.type = "playback";
    } catch (e) {
      /* not supported — nothing to fall back to */
    }

    a.src = MP3;
    setReady(true);

    let cleaned = false;
    const cleanup = () => {
      if (cleaned) return;
      cleaned = true;
      START_EVENTS.forEach((ev) => window.removeEventListener(ev, startOnInteract));
    };
    // Only tear down the listeners once playback has actually started —
    // an attempt can still be rejected, and cleaning up regardless would
    // leave the music permanently silent.
    let inFlight = false;
    const startOnInteract = () => {
      if (inFlight) return;
      inFlight = true;
      a.play()
        .then(() => {
          setPlaying(true);
          cleanup();
        })
        .catch(() => {})
        .finally(() => {
          inFlight = false;
        });
    };

    // Listeners go on before the autoplay attempt, not inside its rejection
    // handler: that promise settles asynchronously, and a guest who taps in the
    // meantime would find nothing listening yet.
    START_EVENTS.forEach((ev) =>
      window.addEventListener(ev, startOnInteract, { once: false, passive: true })
    );
    a.play()
      .then(() => setPlaying(true))
      .catch(() => {});

    return cleanup;
  }, []);

  const toggle = async () => {
    const a = audioRef.current;
    if (!a) return;
    if (playing) {
      a.pause();
      setPlaying(false);
      return;
    }
    try {
      await a.play();
      setPlaying(true);
    } catch (e) {
      setPlaying(false);
    }
  };

  return (
    <div className="fixed bottom-6 right-6 z-[70] flex items-center gap-3">
      <audio ref={audioRef} preload="auto" playsInline />

      <motion.span
        initial={{ opacity: 0, x: 10 }}
        animate={{ opacity: playing ? 0 : 1, x: playing ? 10 : 0 }}
        transition={{ duration: 0.4 }}
        className="hidden select-none rounded-full bg-white/70 px-4 py-2 font-body text-xs tracking-widest text-[var(--maroon)] backdrop-blur-xl sm:block"
      >
        PLAY MUSIC
      </motion.span>

      <motion.button
        data-testid={WEDDING.muteButton}
        onClick={toggle}
        whileHover={{ scale: 1.08 }}
        whileTap={{ scale: 0.94 }}
        disabled={!ready}
        aria-label={playing ? "Mute background music" : "Play background music"}
        className="relative grid h-14 w-14 place-items-center rounded-full border border-white/50 bg-white/70 text-[var(--maroon)] shadow-[0_10px_40px_-8px_rgba(128,0,0,0.4)] backdrop-blur-xl"
      >
        {playing && (
          <motion.span
            className="absolute inset-0 rounded-full border border-[var(--gold)]"
            animate={{ scale: [1, 1.4], opacity: [0.6, 0] }}
            transition={{ duration: 1.8, repeat: Infinity, ease: "easeOut" }}
          />
        )}
        {playing ? (
          <motion.span
            animate={{ rotate: 360 }}
            transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
          >
            <Music2 className="h-5 w-5" strokeWidth={1.6} />
          </motion.span>
        ) : (
          <Volume2 className="h-5 w-5" strokeWidth={1.6} />
        )}
        {playing && (
          <span className="absolute -bottom-1 -right-1 grid h-5 w-5 place-items-center rounded-full bg-[var(--maroon)] text-white">
            <VolumeX className="h-3 w-3" strokeWidth={2} />
          </span>
        )}
      </motion.button>
    </div>
  );
};
